import { action, makeObservable, observable } from 'mobx';
import { v4 } from 'uuid';

import { equalsDeep } from '../../../shared/equalsDeep';
import type { TUserData } from '../../../shared/types';
import type { TAlertRule, TUserInfo, TIndexType, TRiskLevel, TSourceType } from '../../types';

export type TData = { [key: string]: unknown | null } & {
  [Key in keyof Partial<TAlertRule>]: TAlertRule[Key] | null;
} & { riskLevel: TRiskLevel; sourceType: TSourceType; indexType: TIndexType };

export class AlertRule {
  readonly entityId = v4();
  readonly id: string | null;
  readonly initialData: TData;

  createdBy: TUserInfo;

  createdAt: string;

  updatedBy: TUserInfo | null;

  updatedAt: string | null;

  data: TData;

  dirty = false;

  notificationsEnabled = true;

  constructor(initialData: TData, userData: TUserData, id?: string) {
    this.data = initialData;
    this.initialData = initialData;
    this.id = id ?? null;
    this.createdBy = initialData.createdBy ?? {
      firstName: userData.given_name,
      lastName: userData.family_name,
      sub: userData.sub,
      username: userData.preferred_username,
    };
    this.createdAt = initialData.createdAt ?? new Date().toISOString();
    this.updatedBy = initialData.updatedBy ?? null;
    this.updatedAt = initialData.updatedAt ?? null;
    makeObservable(this, {
      data: observable,
      updateData: action,
      notificationsEnabled: observable,
      setNotificationsEnabled: action,
    });
  }

  updateData(updateData: Partial<TData>): void {
    this.data = {
      ...this.data,
      ...updateData,
    };
    this.dirty = true;
  }

  resetDirty(): void {
    this.dirty = false;
  }

  deepEquals(other: AlertRule): boolean {
    return equalsDeep(this.data, other.data);
  }

  setNotificationsEnabled(enabled: boolean): void {
    this.notificationsEnabled = enabled;
  }

  get isChanged(): boolean {
    return this.dirty && !equalsDeep(this.data, this.initialData);
  }

  static hasContext(alertRule: AlertRule): boolean {
    const data = alertRule.data;
    return (
      data &&
      ((data.stageIds || []).length > 0 ||
        (data.wellboreTypeIds || []).length > 0 ||
        (data.trajectoryTypeIds || []).length > 0 ||
        (data.sectionTypeIds || []).length > 0 ||
        (data.stratUnitTypeIds || []).length > 0 ||
        (data.reservoirIds || []).length > 0 ||
        (data.runTypeIds || []).length > 0 ||
        (data.operationTypeIds || []).length > 0 ||
        (data.holeTypes || []).length > 0)
    );
  }
}
